import React, { useEffect, useState } from 'react';
import { ArrowRight, Clock, Mail, MapPin, Phone, Send } from 'lucide-react';

import Header from '../components/Header';
import Footer from '../components/Footer';
import PageBanner from '../components/PageBanner';
import Seo, { breadcrumbSchema, SITE_URL } from '../components/Seo';
import { submitContactMessage } from '../api/messages';
import { CONTACT_DETAILS } from '../data/aboutData';
import { COMPANY_PROFILE } from '../data/aboutCompany';
import { PAGE_BANNER_IMAGES } from '../data/homeData';

import '../Home.css';
import '../PageBanner.css';
import '../ContactPage.css';

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  message: '',
};

function ContactPage() {
  const description = 'Contact Swarvinn Impex for UK wholesale prices, private-label packing, Indian grocery sourcing and India-to-UK freight options.';
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (status.text) setStatus({ type: '', text: '' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus({ type: '', text: '' });

    try {
      await submitContactMessage(form);
      setForm(INITIAL_FORM);
      setStatus({
        type: 'success',
        text: 'Thank you — your enquiry has been received. Our export team will respond within 1–2 business days.',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        text: error.message || 'Something went wrong. Please try again or email us directly.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page contact-page">
      <Seo
        title="Contact Our Indian Grocery Export Team"
        description={description}
        path="/contact"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: 'Contact Swarvinn Impex',
            url: `${SITE_URL}/contact`,
            description,
            inLanguage: 'en-GB',
            about: { '@id': `${SITE_URL}/#organization` },
          },
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Contact', path: '/contact' },
          ]),
        ]}
      />
      <Header />
      <main className="main">
        <PageBanner
          image={PAGE_BANNER_IMAGES.contact}
          eyebrow="Get in Touch"
          title="Contact Swarvinn Impex"
          lead="Request a wholesale import quote for the UK (our main market), ask about private labelling, or speak with our export team about freight to secondary markets and documentation."
          breadcrumbs={[{ label: 'Contact' }]}
        />

        <section className="contact-main">
          <div className="contact-grid">
            <div className="contact-form-card">
              <h2>Send an enquiry</h2>
              <p>
                Share your product list, quantities, and packing preferences. MOQ is{' '}
                {COMPANY_PROFILE.moq}.
              </p>

              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="contact-field">
                  <label htmlFor="contact-name">Full name</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your name"
                  />
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-email">Email address</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@company.co.uk"
                  />
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-phone">Phone (optional)</label>
                  <input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+44 or +91 number"
                  />
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-message">Your message</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Products needed, quantities, packing format, UK delivery location…"
                  />
                </div>

                {status.text && (
                  <p className={`contact-status contact-status--${status.type}`} role="status">
                    {status.text}
                  </p>
                )}

                <button type="submit" className="contact-submit" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send enquiry'}
                  {!submitting && <Send size={16} />}
                </button>
              </form>
            </div>

            <aside className="contact-aside">
              <div className="contact-info-card">
                <h3>Direct contact</h3>
                <ul className="contact-info-list">
                  <li>
                    <MapPin size={18} />
                    <span>{CONTACT_DETAILS.address}</span>
                  </li>
                  <li>
                    <Phone size={18} />
                    <a href={`tel:${CONTACT_DETAILS.phone.replace(/\s/g, '')}`}>
                      {CONTACT_DETAILS.phone}
                    </a>
                  </li>
                  <li>
                    <Mail size={18} />
                    <a href={`mailto:${CONTACT_DETAILS.email}`}>{CONTACT_DETAILS.email}</a>
                  </li>
                  <li>
                    <Clock size={18} />
                    <span>Mon–Sat, 9:00–18:00 IST · Response within 1–2 business days</span>
                  </li>
                </ul>
              </div>

              <div className="contact-info-card contact-highlights">
                <h3>What to include</h3>
                <ul>
                  <li>Product categories and SKUs required</li>
                  <li>Wholesale quantities per item</li>
                  <li>Preferred packing (retail, bulk, private label)</li>
                  <li>UK delivery port or warehouse location</li>
                </ul>
              </div>

              <a href={`mailto:${CONTACT_DETAILS.email}`} className="about-read-more">
                Email for urgent quotes
                <ArrowRight size={16} />
              </a>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default ContactPage;
